import { gql } from '@apollo/client';

// Types
export type CasePriority = 'low' | 'medium' | 'high' | 'urgent';
export type CaseStatus = 'new' | 'open' | 'pending' | 'resolved' | 'closed';
export type CaseType = 'software' | 'services';
export type CaseActivityType = 'reply' | 'internal_note' | 'status_change' | 'assignment';

export interface CaseActivity {
  id: string;
  caseId: string;
  type: CaseActivityType;
  content: string | null;
  userId: string | null;
  userName: string;
  oldStatus: CaseStatus | null;
  newStatus: CaseStatus | null;
  oldAssigneeId: string | null;
  oldAssigneeName: string | null;
  newAssigneeId: string | null;
  newAssigneeName: string | null;
  createdAt: string;
}

export interface CaseOrganization {
  id: string;
  name: string;
}

export interface CaseContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  title: string | null;
}

export interface Case {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string | null;
  organizationId: string;
  organization?: CaseOrganization | null;
  contactId: string | null;
  contact?: CaseContact | null;
  caseType: CaseType;
  priority: CasePriority;
  status: CaseStatus;
  assignedToId: string | null;
  assignedToName: string | null;
  blastAppNumber: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
  activities?: CaseActivity[];
}

export interface CaseStats {
  total: number;
  active: number;
  urgent: number;
  unassigned: number;
}

// Query Response Types
export interface GetCasesData {
  cases: {
    items: Case[];
    pageInfo: {
      totalCount: number;
      totalPages: number;
      currentPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export interface GetCasesVariables {
  search?: string;
  status?: string;
  priority?: CasePriority;
  organizationId?: string;
  assignedToId?: string;
  limit?: number;
  offset?: number;
}

export interface GetCaseData {
  case: Case | null;
}

export interface GetCaseStatsData {
  caseStats: CaseStats;
}

export interface CreateCaseData {
  createCase: Case;
}

export interface UpdateCaseData {
  updateCase: Case | null;
}

export interface DeleteCaseData {
  deleteCase: boolean;
}

export interface AddCaseActivityData {
  addCaseActivity: CaseActivity;
}

// Fragments
const CASE_FRAGMENT = gql`
  fragment CaseFields on Case {
    id
    ticketNumber
    subject
    description
    organizationId
    organization {
      id
      name
    }
    contactId
    contact {
      id
      firstName
      lastName
      email
      phone
      title
    }
    caseType
    priority
    status
    assignedToId
    assignedToName
    blastAppNumber
    createdAt
    updatedAt
    resolvedAt
    closedAt
  }
`;

const ACTIVITY_FRAGMENT = gql`
  fragment ActivityFields on CaseActivity {
    id
    caseId
    type
    content
    userId
    userName
    oldStatus
    newStatus
    oldAssigneeId
    oldAssigneeName
    newAssigneeId
    newAssigneeName
    createdAt
  }
`;

// Queries
export const GET_CASES = gql`
  ${CASE_FRAGMENT}
  query GetCases(
    $search: String
    $status: String
    $priority: CasePriority
    $organizationId: ID
    $assignedToId: String
    $limit: Int
    $offset: Int
  ) {
    cases(
      search: $search
      status: $status
      priority: $priority
      organizationId: $organizationId
      assignedToId: $assignedToId
      limit: $limit
      offset: $offset
    ) {
      items {
        ...CaseFields
      }
      pageInfo {
        totalCount
        totalPages
        currentPage
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_CASE = gql`
  ${CASE_FRAGMENT}
  ${ACTIVITY_FRAGMENT}
  query GetCase($id: ID!) {
    case(id: $id) {
      ...CaseFields
      activities {
        ...ActivityFields
      }
    }
  }
`;

export const GET_CASE_STATS = gql`
  query GetCaseStats {
    caseStats {
      total
      active
      urgent
      unassigned
    }
  }
`;

// Mutations
export const CREATE_CASE = gql`
  ${CASE_FRAGMENT}
  mutation CreateCase($input: CreateCaseInput!) {
    createCase(input: $input) {
      ...CaseFields
    }
  }
`;

export const UPDATE_CASE = gql`
  ${CASE_FRAGMENT}
  mutation UpdateCase($id: ID!, $input: UpdateCaseInput!) {
    updateCase(id: $id, input: $input) {
      ...CaseFields
    }
  }
`;

export const DELETE_CASE = gql`
  mutation DeleteCase($id: ID!) {
    deleteCase(id: $id)
  }
`;

export const ADD_CASE_ACTIVITY = gql`
  ${ACTIVITY_FRAGMENT}
  mutation AddCaseActivity($input: AddCaseActivityInput!) {
    addCaseActivity(input: $input) {
      ...ActivityFields
    }
  }
`;
